// src/components/feed/SessionRecapBody.tsx
//
// Body renderer for kind='session_recap' feed items. Replaces the
// default snapshot.image/title/caption pattern with a denser layout:
//   - small stat strip (visits, distance, duration)
//   - prose paragraph
//   - optional "next time" hook as a tappable suggestion
//   - tap whole card → opens the originating conversation
//
// The card itself (avatar, header strip, action row, footer) is
// still handled by FeedItemCard; this is what slots into the
// snapshot/body region.

import React from 'react';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import {
  PlaceOutlined,
  DirectionsWalk,
  AccessTime,
  AutoAwesome,
  ArrowForward,
} from '@mui/icons-material';
import { FeedItem } from '../../feed/types';
import { useT } from '../../i18n/useT';

interface Props {
  item: FeedItem;
  /** Called when the user taps the "next" hook to push it into
   *  chat. Optional — the card still renders the hook as static
   *  text when no handler is provided. */
  onNextHook?: (prompt: string) => void;
}

const formatDistance = (m: number): string => {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
};

export const SessionRecapBody: React.FC<Props> = ({ item, onNextHook }) => {
  const t = useT();
  const theme = useTheme();
  const recap = item.recap;
  const snapshot = item.snapshot;
  if (!recap) return null;

  const placeName = snapshot.location?.place_name;
  const finds = recap.finds || [];
  // The "next time" hook lives in snapshot.body (we stored it there
  // server-side so generic consumers see it as the "secondary text"
  // field). The renderer can pick it up and treat as a CTA.
  const nextHook = (snapshot.body || '').trim();

  return (
    <Box sx={{ p: 2 }}>
      {/* Title line */}
      {snapshot.title && (
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.25 }}
        >
          {snapshot.title}
        </Typography>
      )}

      {/* Stat strip — small, monospace-ish, visually distinct from
          the prose. Communicates "this is a quantified recap, not a
          piece of conversation." */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.25,
          mb: 1.25,
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
          }}
        >
          <PlaceOutlined sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {t('{count} stops', { count: recap.visit_count })}
          </Typography>
        </Box>
        {recap.distance_m > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
            }}
          >
            <DirectionsWalk sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {formatDistance(recap.distance_m)}
            </Typography>
          </Box>
        )}
        {recap.duration_min > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
            }}
          >
            <AccessTime sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {recap.duration_min < 60
                ? `${recap.duration_min}m`
                : `${Math.floor(recap.duration_min / 60)}h ${recap.duration_min % 60}m`}
            </Typography>
          </Box>
        )}
        {placeName && (
          <Typography variant="caption" color="text.disabled">
            · {placeName}
          </Typography>
        )}
      </Box>

      {/* Find chips — denormalized list of collection finds this
          session. Compact: at most 3 visible, "+N more" overflow. */}
      {finds.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
          {finds.slice(0, 3).map((f, i) => (
            <Chip
              key={i}
              size="small"
              icon={<AutoAwesome sx={{ fontSize: 12 }} />}
              label={f.item_title}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiChip-icon': { color: 'primary.contrastText', ml: 0.5 },
              }}
            />
          ))}
          {finds.length > 3 && (
            <Chip
              size="small"
              label={t('+{n} more', { n: finds.length - 3 })}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: 'action.hover',
              }}
            />
          )}
        </Box>
      )}

      {/* Prose — the recap's main content */}
      {snapshot.caption && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            lineHeight: 1.55,
            mb: nextHook ? 1.5 : 0,
          }}
        >
          {snapshot.caption}
        </Typography>
      )}

      {/* Next-time hook — when present, render as a tappable
          suggestion that sends it into chat. Falls back to static
          quoted text when no handler is wired. */}
      {nextHook && onNextHook && (
        <Box
          onClick={() => onNextHook(nextHook)}
          role="button"
          tabIndex={0}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            cursor: 'pointer',
            p: 1.25,
            mt: 0.5,
            borderRadius: 2,
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.03)',
            border: '1px solid',
            borderColor: theme.palette.divider,
            transition: 'background-color 120ms ease-out',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <AutoAwesome
            sx={{ fontSize: 16, color: 'primary.main', mt: 0.25, flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              color: 'text.primary',
              fontStyle: 'italic',
              lineHeight: 1.4,
            }}
          >
            {nextHook}
          </Typography>
          <ArrowForward
            sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }}
          />
        </Box>
      )}
      {nextHook && !onNextHook && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          {nextHook}
        </Typography>
      )}
    </Box>
  );
};
